/**
 * 版本信息工具函数
 *
 * 版本信息自动从构建时注入，无需手动维护：
 * - 版本号 (version): 来自 package.json
 * - 构建时间 (buildTime): 自动生成
 * - Git Commit (commit): 来自 Vercel 环境变量或本地 Git
 * - Git Branch (branch): 来自 Vercel 环境变量或本地 Git
 * - 环境 (env): production / preview / development
 */

export interface VersionInfo {
  version: string;
  buildTime: string;
  commit: string;
  branch: string;
  env: string;
}

/**
 * 获取完整版本信息
 */
export function getVersionInfo(): VersionInfo {
  return {
    version: __APP_VERSION__,
    buildTime: __BUILD_TIME__,
    commit: __GIT_COMMIT__,
    branch: __GIT_BRANCH__,
    env: __VERCEL_ENV__,
  };
}

/**
 * 获取简短版本字符串
 * 格式: "0.1.0 (a1b2c3d)"
 */
export function getVersionString(): string {
  const info = getVersionInfo();
  if (info.commit === 'local') {
    return `${info.version} (local)`;
  }
  return `${info.version} (${info.commit})`;
}

/**
 * 格式化构建时间为中文友好格式
 */
export function formatBuildTime(isoString: string): string {
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return isoString;
  }
}

/**
 * 检查是否有新版本可用
 * 通过比较本地 commit 和服务器 version.json 的 commit
 */
export async function checkForUpdate(): Promise<{
  hasUpdate: boolean;
  currentVersion: string;
  currentCommit: string;
  latestCommit?: string;
}> {
  const info = getVersionInfo();

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    const response = await fetch('/version.json?t=' + Date.now(), {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      return { hasUpdate: false, currentVersion: info.version, currentCommit: info.commit };
    }

    const data = await response.json();
    const latestCommit = data.commit;

    const hasUpdate = latestCommit &&
      latestCommit.toLowerCase() !== info.commit.toLowerCase() &&
      latestCommit !== 'local';

    return {
      hasUpdate,
      currentVersion: info.version,
      currentCommit: info.commit,
      latestCommit,
    };
  } catch {
    return { hasUpdate: false, currentVersion: info.version, currentCommit: info.commit };
  }
}

/** 判断是否为生产环境 */
export function isProduction(): boolean {
  return __VERCEL_ENV__ === 'production';
}

/** 判断是否为预览环境 */
export function isPreview(): boolean {
  return __VERCEL_ENV__ === 'preview';
}

/** 判断是否为本地开发环境 */
export function isDevelopment(): boolean {
  return __VERCEL_ENV__ === 'development';
}
